export interface TourStep {
  title: string;
  description: string;
  /** SPA route, e.g. "/orders" or "/stores/<id>/orders". */
  route: string;
  /** Optional query params appended to the route. */
  queryParams?: Record<string, string>;
  /** Id of a `.mock-data/<id>.json` fixture applied for this step. */
  fixture?: string;
}

export interface TourManifest {
  /** Optional human-readable name shown in the bar header. */
  title?: string;
  steps: TourStep[];
}

/** A `.mock-data/<id>.json` file is a tour when it has this shape. */
export interface TourFixtureFile {
  tour: TourManifest;
}

export function isTourFixtureFile(value: unknown): value is TourFixtureFile {
  return (
    typeof value === "object" &&
    value !== null &&
    "tour" in value &&
    typeof (value as { tour?: unknown }).tour === "object" &&
    (value as { tour?: unknown }).tour !== null &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    Array.isArray((value as TourFixtureFile).tour.steps)
  );
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isValidStep(value: unknown): value is TourStep {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const step = value as Record<string, unknown>;
  return (
    isNonEmptyString(step.title) &&
    isNonEmptyString(step.description) &&
    isNonEmptyString(step.route) &&
    (step.fixture === undefined || typeof step.fixture === "string") &&
    (step.queryParams === undefined ||
      (typeof step.queryParams === "object" && step.queryParams !== null))
  );
}

export type TourParseResult =
  | { ok: true; manifest: TourManifest }
  | { ok: false; reason: string };

/**
 * Validate a parsed `.mock-data/<id>.json` as a tour. Tours require at least
 * `minSteps` (a single-page change should use `?mock=<id>`, not a tour) and
 * every step must carry a title, description, and route.
 */
export function parseTourManifest(
  value: unknown,
  minSteps = 2,
): TourParseResult {
  if (!isTourFixtureFile(value)) {
    return { ok: false, reason: "not a tour fixture" };
  }
  const { steps } = value.tour;
  if (!steps.every(isValidStep)) {
    return {
      ok: false,
      reason: "a step is missing title, description, or route",
    };
  }
  if (steps.length < minSteps) {
    return {
      ok: false,
      reason: `has ${steps.length} step(s); tours need at least ${minSteps}`,
    };
  }
  return { ok: true, manifest: value.tour };
}

/**
 * Build the SPA path for a step, merging the step's `queryParams` and
 * preserving the `mock`/`tour` params so a reload or shared link resumes the
 * tour rather than dropping it. Later sources win on duplicate keys.
 */
export function buildStepUrl(
  step: TourStep,
  preserve: { tourId?: string; mockParam?: string },
): string {
  const qIdx = step.route.indexOf("?");
  const path = qIdx >= 0 ? step.route.slice(0, qIdx) : step.route;
  const params = new URLSearchParams(
    qIdx >= 0 ? step.route.slice(qIdx + 1) : "",
  );
  if (step.queryParams) {
    for (const [key, value] of Object.entries(step.queryParams)) {
      params.set(key, value);
    }
  }
  if (preserve.mockParam) {
    params.set("mock", preserve.mockParam);
  }
  if (preserve.tourId) {
    params.set("tour", preserve.tourId);
  }
  const search = params.toString();
  return search ? `${path}?${search}` : path;
}
